/* Shared report widgets: back bar, mini stat cell, and the keyword table used by both
   TopicDetail and PageDetail — exact ports from code.txt. */

import { Card, Lbl, SectionTitle, TH, TD, Pill, T, MONO, FONT, fmt, escapeHtml, ExportBar, AioLabel } from "../../ui.js";

export function BackBar(label, attrs) {
  return `<button ${attrs} style="display:inline-flex;align-items:center;gap:6px;border:none;background:transparent;color:${T.sub};font-family:${FONT};font-size:12.5px;font-weight:700;cursor:pointer;padding:0;margin-bottom:14px">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
    ${label}
  </button>`;
}

/* Mini stat cell used inside clubbed metric boxes (TopicDetail "What we found", Pages detail). */
export function MiniStat(label, value, { sub, ai = false } = {}) {
  return `<div style="background:${ai ? T.magentaSoft : T.bg};border:1px solid ${ai ? "rgba(245,15,175,0.15)" : T.lineSoft};border-radius:10px;padding:9px 11px;min-width:0">
    ${Lbl(label, `margin-bottom:2px;${ai ? `color:${T.magenta}` : ""}`)}
    <div style="font-family:${MONO};font-size:15px;font-weight:800;color:${ai ? T.magenta : T.ink};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${value}</div>
    ${sub ? `<div style="font-size:10px;color:${T.muted};margin-top:1px">${sub}</div>` : ""}
  </div>`;
}

/* The shared keyword table under a topic or page: headers, aligned numeric columns,
   sticky head, export. hasSerp adds the AI Overview / cited columns. */
export function KeywordTable({ title, queries, hasSerp, exportBase, note }) {
  const rows = [...queries].sort((a, b) => b.impressions - a.impressions);
  const exportRowsData = rows.map(q => ({
    keyword: q.query, impressions: q.impressions, clicks: q.clicks,
    ctr_pct: q.impressions ? +(100 * q.clicks / q.impressions).toFixed(2) : 0, avg_position: q.position,
    ...(hasSerp ? { ai_overview_shown: q.aio == null ? "" : q.aio ? "yes" : "no", cited_in_aio: q.cited == null ? "" : q.cited ? "yes" : "no" } : {}),
  }));
  const head = SectionTitle(
    `${title} <span style="font-family:${MONO};color:${T.muted};font-weight:600">(${rows.length})</span>`,
    { style: "margin-bottom:0" },
  );
  return Card(`
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px 10px">
      ${head}
      ${ExportBar(exportRowsData, exportBase)}
    </div>
    <div style="max-height:480px;overflow-y:auto">
      <table style="width:100%;border-collapse:collapse">
        <thead style="position:sticky;top:0;background:${T.surface};z-index:1">
          <tr>
            ${TH("Keyword")}
            ${hasSerp ? TH(AioLabel("AI Overview", 10)) : ""}
            ${TH("Impressions", "text-align:right")}
            ${TH("Clicks", "text-align:right")}
            ${TH("CTR", "text-align:right")}
            ${TH("Position", "text-align:right")}
          </tr>
        </thead>
        <tbody>
          ${rows.map(q => `
            <tr>
              ${TD(escapeHtml(q.query), { style: "font-size:12.5px;max-width:320px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" })}
              ${hasSerp ? TD(
                q.aio == null
                  ? Pill(T.muted, T.lineSoft, "not checked")
                  : q.aio
                    ? Pill(T.magenta, T.magentaSoft, AioLabel(q.cited ? "AIO · cited" : "AIO shown", 10))
                    : Pill(T.muted, T.lineSoft, "no AIO"),
              ) : ""}
              ${TD(fmt(q.impressions), { mono: true, style: "text-align:right;font-size:12px" })}
              ${TD(fmt(q.clicks), { mono: true, style: "text-align:right;font-size:12px" })}
              ${TD(q.impressions ? (100 * q.clicks / q.impressions).toFixed(1) + "%" : "\u2014", { mono: true, style: `text-align:right;font-size:12px;color:${T.muted}` })}
              ${TD(q.position || "\u2014", { mono: true, style: "text-align:right;font-size:12px" })}
            </tr>`).join("")}
        </tbody>
      </table>
    </div>
    ${note ? `<div style="font-size:10.5px;color:${T.muted};padding:9px 16px;border-top:1px solid ${T.lineSoft}">${note}</div>` : ""}`,
  { style: "padding:0;overflow:hidden;margin-bottom:20px" });
}
